// src/pages/xray/XrayLobby/utils.js
export const processFileLines = content => {
  const lines = content.split('\n');
  const sectionConfigs = [
    {
      name: 'defaultVLAN',
      start: ['FILE:/etc/config/ip.conf'],
      end: ['FILE:/etc/config/ipsec.conf.orig'],
      lines: [],
    },
    {
      name: 'membersVLAN',
      start: ['FILE:/etc/config/vlan_member.conf'],
      end: ['FILE:/etc/config/vlan_net.conf'],
      lines: [],
    },
    {
      name: 'tagVLAN',
      start: ['FILE:/etc/config/vlan_port.conf'],
      end: ['FILE:/etc/config/vpn.conf'],
      lines: [],
    },
    {
      name: 'vlanNetworks',
      start: ['FILE:/etc/config/vlan_net.conf'],
      end: ['FILE:/etc/config/vlan_port.conf'],
      lines: [],
    },
    {
      name: 'packetsDropped',
      start: ['=== INTERFACES ==='],
      end: ['=== INTERFACES: link status ==='],
      lines: [],
    },
    {
      name: 'wanFailover',
      start: ['FILE:/etc/config/intf.conf'],
      end: ['PRI_WAN_INTF6='],
      lines: [],
    },
    {
      name: 'voiceVsDataTraffic',
      start: ['PRI_WAN_INTF6='],
      end: ['PRI_WAN_IP6='],
      lines: [],
    },
    {
      name: 'routingTable',
      start: ['=== ROUTES ==='],
      end: ['=== Advanced IPv4 routing tables ==='],
      lines: [],
    },
    {
      name: 'cidrRanges',
      start: ['=== INTERFACES: IP command ==='],
      end: ['=== INTERFACES: IPv6 command ==='],
      lines: [],
    },
    {
      name: 'arpTable',
      start: ['=== ARP ==='],
      end: ['=== CONNTRACK TABLE ==='],
      lines: [],
    },
    {
      name: 'scopesDHCPLimits',
      start: ['FILE:/etc/config/dhcpd_defs_lan.conf'],
      end: ['=== End of ewn save ==='],
      lines: [],
    },
    {
      name: 'leasesDHCPRawDataLimits',
      start: ['=== DHCP leases ==='],
      end: ['=== SYSLOGS ==='],
      lines: [],
    },
    {
      name: 'sipServerLimits',
      start: ['/var/mand/sipservers_data'],
      end: ['/var/mand/sipservers_status'],
      lines: [],
    },
    {
      name: 'sipClientsLimits',
      start: ['FILE:/etc/config/sipclients.2'],
      end: ['=== Start of ewn save ==='],
      lines: [],
    },
    {
      name: 'loggedErrorsLimits',
      start: ['=== SYSLOGS ==='],
      end: ['=== KERNEL CRASH DUMP ==='],
      lines: [],
    },
    {
      name: 'conntrackTableLimits',
      start: ['=== CONNTRACK TABLE ==='],
      end: ['=== TINFO ==='],
      lines: [],
    },
    {
      name: 'proxyARPRulesLimits',
      start: ['FILE:/etc/config/proxy_arp_defs.conf'],
      end: ['FILE:/etc/config/reboot_details.conf'],
      lines: [],
    },
    {
      name: 'passthroughRulesLimits',
      start: ['FILE:/etc/config/fwd_defs.conf'],
      end: ['FILE:/etc/config/group'],
      lines: [],
    },
    {
      name: 'userCommandsLimits',
      start: ['FILE:/etc/config/user_defs.conf'],
      end: ['FILE:/etc/config/user_mgmt.conf'],
      lines: [],
    },
    {
      name: 'vosImgVersionLimits',
      start: ['=== Version ==='],
      end: ['=== Vendor ==='],
      lines: [],
    },
  ];

  const captureMap = {};
  sectionConfigs.forEach(section => {
    captureMap[section.name] = false;
  });

  lines.forEach(line => {
    sectionConfigs.forEach(section => {
      const { name, start, end } = section;
      if (start?.some(startStr => line.includes(startStr))) {
        captureMap[name] = true;
        return;
      }
      if (end?.some(endStr => line.includes(endStr))) {
        captureMap[name] = false;
        return;
      }
      if (captureMap[name]) {
        section.lines.push(line);
      }
    });
  });

  const dhcpLeasesLines =
    sectionConfigs.find(s => s.name === 'leasesDHCPRawDataLimits')?.lines || [];
  const formattedLeasesString = dhcpLeasesLines
    .join(' ')
    .replace(/lease/g, '\nlease');

  const sortedLines = formattedLeasesString.split('\n').sort((a, b) => {
    const extractIP = line => {
      const match = line.match(/\b\d{1,3}(?:\.\d{1,3}){3}\b/);
      return match ? match[0] : '0.0.0.0';
    };
    const ipToNumeric = ip =>
      ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet, 10), 0);
    return ipToNumeric(extractIP(a)) - ipToNumeric(extractIP(b));
  });

  const hiddenLeases = sortedLines
    .map(line => line.replace(/({)(.*?)(ethernet)/, '$1 [hidden] $3'))
    .join('\n');

  const boldedOutput = hiddenLeases.replace(
    /(yealink|Grandstream|CP-|MP|cisco|polycom|switch)/gi,
    '<b>$1</b>'
  );

  let uptimeStatus = 'Not found';
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes('=== System Uptime ===')) {
      uptimeStatus = lines[i + 1]?.trim() || 'Not found';
      break;
    }
  }

  const match = uptimeStatus.match(/up([^,]*)/);
  if (match) {
    uptimeStatus = match[1].trim();
  }

  function extractInetAddr(content, voipVlanValue) {
    const lines = content.split('\n');
    const vlanLineIndex = lines.findIndex(line =>
      line.trim().startsWith(voipVlanValue)
    );
    if (vlanLineIndex !== -1 && lines[vlanLineIndex + 1]) {
      const nextLine = lines[vlanLineIndex + 1];
      const match = nextLine.match(/inet addr:([\d.]+).*Bcast/);
      if (match) return match[1].trim();
    }
    return null;
  }

  const voiceVlanValue = content.match(/ALG_VLAN=(.*)/)?.[1]?.trim();
  const regServerOneIp = voiceVlanValue
    ? extractInetAddr(content, voiceVlanValue)
    : null;

  const passthroughSection = sectionConfigs.find(
    section => section.name === 'passthroughRulesLimits'
  );
  const passthroughRulesLimits =
    passthroughSection?.lines?.join('\n') || 'Not found';

  const proxyARPSection = sectionConfigs.find(
    section => section.name === 'proxyARPRulesLimits'
  );
  const proxyARPRulesLimits = proxyARPSection?.lines?.join('\n') || 'Not found';

  const userCommandsSection = sectionConfigs.find(
    section => section.name === 'userCommandsLimits'
  );
  const userCommandsLimits =
    userCommandsSection?.lines?.join('\n') || 'Not found';

  const vosImgVersionSection = sectionConfigs.find(
    section => section.name === 'vosImgVersionLimits'
  );
  const vosImgVersionLimits =
    vosImgVersionSection?.lines?.join('\n') || 'Not found nope nada';

  const deviceOverview = {
    hostname: content.match(/LOCAL_HOSTNAME=(.*)/)?.[1] || 'Not found',
    Uptime: uptimeStatus,
    'LAN MAC': content.match(/LANMAC=(.*)/)?.[1] || 'Not found',
    Model: content.match(/EWN_MODEL=(.*)/)?.[1] || 'Not found',
    'regserver-1': regServerOneIp || 'Not found',
    'SIP Proxy Port': content.match(/sipproxy-port=(.*)/)?.[1] || 'Not found',
    'snmp Community': content.match(/SNMP_RO_COMM=(.*)/)?.[1] || 'Not found',
    'Voice vlan': content.match(/ALG_VLAN=(.*)/)?.[1] || 'Not found',
    'Primary Wan Ip': content.match(/PRI_WAN_IP=(.*)/)?.[1] || 'Not found',
    'Secondary Wan Ip': content.match(/SEC_WAN_IP=(.*)/)?.[1] || 'Not found',
    'Passthrough Rules': passthroughRulesLimits,
    'Proxy ARP': proxyARPRulesLimits,
    'User Commands': userCommandsLimits,
    'VOS Image Version': vosImgVersionLimits,
  };

  const targetPrefix = 'ORG_REMOTE_SYSLOG_HOSTS=';
  let syslogHostLine = '';
  for (const line of lines) {
    if (line.trim().startsWith(targetPrefix)) {
      syslogHostLine = line.trim().replace(targetPrefix, '').trim();
      break;
    }
  }

  const sfpToFiberAdapterLines = [];
  let foundSfpStatusSection = false;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes('=== SFP status ===')) {
      foundSfpStatusSection = true;
      continue;
    }
    if (foundSfpStatusSection && !lines[i].includes('=== NETSTAT INFO ===')) {
      sfpToFiberAdapterLines.push(lines[i]);
    }
    if (lines[i].includes('=== NETSTAT INFO ===')) break;
  }
  const sfpStatus =
    sfpToFiberAdapterLines.length > 0
      ? sfpToFiberAdapterLines.join('\n')
      : 'No SFP status found.';

  let wanLines = [];
  let captureWAN = false;
  for (const line of lines) {
    if (line.includes('=== INTERFACES: link status ===')) {
      captureWAN = true;
      wanLines.push(line.trim());
    } else if (captureWAN && line.trim() === '') {
      captureWAN = false;
    } else if (captureWAN) {
      wanLines.push(line.trim());
    }
  }

  wanLines = wanLines
    .filter(line => line.startsWith('eth1:') || line.startsWith('eth2:'))
    .sort(a => (a.startsWith('eth1:') ? -1 : 1));

  let lanLines = [];
  let captureLAN = false;
  for (const line of lines) {
    if (line.includes('=== LAN switch port status ===')) {
      captureLAN = true;
      lanLines.push(line.trim());
    } else if (captureLAN && line.trim() === '') {
      captureLAN = false;
    } else if (captureLAN) {
      lanLines.push(line.trim());
    }
  }

  let cidrRangesLines = [];
  let foundIPCommandSection = false;
  for (let i = 0; i < lines.length; i++) {
    if (lines[i].includes('=== INTERFACES: IP command ===')) {
      foundIPCommandSection = true;
      continue;
    }
    if (
      foundIPCommandSection &&
      !lines[i].includes('=== INTERFACES: IPv6 command ===')
    ) {
      if (lines[i].includes('global')) {
        cidrRangesLines.push(lines[i] + '\n');
      }
    }
    if (lines[i].includes('=== INTERFACES: IPv6 command ===')) break;
  }

  const parsedSections = sectionConfigs.reduce((acc, section) => {
    let joinedLines = section.lines.join('\n');
    if (section.name === 'loggedErrorsLimits') {
      joinedLines = joinedLines
        .split('\n')
        .filter(
          line =>
            !/^\w{3} \d{1,2} \d{2}:\d{2}:\d{2} \d{4} .* last message repeated \d+ times$/i.test(
              line
            )
        )
        .join('\n')
        .trim();
    }
    acc[section.name] = joinedLines;
    return acc;
  }, {});

  // *** WAN Failover - Circuit Redundancy ***
  if (parsedSections.voiceVsDataTraffic) {
    parsedSections.voiceVsDataTraffic = parsedSections.voiceVsDataTraffic
      .split('\n')
      .filter(
        line => line.startsWith('VOICE_INTF=') || line.startsWith('DATA_INTF=')
      )
      .join('\n');
  }

  const wanFailoverLines = parsedSections.wanFailover
    ? parsedSections.wanFailover
        .split('\n')
        .map(line => line.trim())
        .filter(Boolean)
    : [];

  const priWanFailoverLines = wanFailoverLines.filter(line =>
    line.includes('PRI')
  );
  const secWanFailoverLines = wanFailoverLines.filter(line =>
    line.includes('SEC')
  );

  // *** DHCP Leases TABLE ***
  const rawDhcpLines = parsedSections.leasesDHCPRawDataLimits
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  const parseDhcpLeasesToJson = dhcpLines => {
    const leases = [];
    const leaseBlocks = dhcpLines.join(' ').split('lease').filter(Boolean);
    leaseBlocks.forEach(block => {
      const ip = block.match(/\b\d{1,3}(?:\.\d{1,3}){3}\b/)?.[0] ?? 'Unknown';
      const mac = block.match(/ethernet\s+([0-9a-f:]+)/i)?.[1] ?? 'Unknown';
      const hostname =
        block.match(/client-hostname\s+"([^"]+)"/i)?.[1] ?? 'Unknown';
      const vendorMatch = hostname?.toLowerCase();
      const model = block.match(/vendor-class\s+"([^"]+)"/i)?.[1] ?? 'Unknown';
      let vendor = 'Unknown';
      if (vendorMatch?.includes('yealink')) vendor = 'Yealink';
      else if (vendorMatch?.includes('cisco')) vendor = 'Cisco';
      else if (vendorMatch?.includes('polycom')) vendor = 'Polycom';
      else if (vendorMatch?.includes('grandstream')) vendor = 'Grandstream';
      else if (model.includes('Polycom')) vendor = 'Polycom';
      else if (model.includes('CP-')) vendor = 'Cisco';
      else if (model.includes('MSFT')) vendor = 'Microsoft';
      leases.push({ ip, mac, hostname, vendor, model });
    });
    return leases;
  };

  const parsedDhcpLeases = parseDhcpLeasesToJson(rawDhcpLines);

  // *** ARP Table Parsing ***
  const rawArpLines = parsedSections.arpTable
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);

  const parseArpTableToJson = arpLines => {
    const arpEntries = [];
    arpLines.forEach(line => {
      // Match typical ARP table format: IP address ? (MAC) [HWtype] [Flags] Interface
      // Example: 192.168.1.1 ether aa:bb:cc:dd:ee:ff C eth0
      const match = line.match(/^(\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3})\s+\S+\s+([0-9a-fA-F:]+)\s+\S+\s+(\S+)$/);
      if (match) {
        arpEntries.push({
          ip: match[1],
          mac: match[2],
          interface: match[3]
        });
      }
    });
    return arpEntries;
  };

  const parsedArpTable = parseArpTableToJson(rawArpLines);

  // *** Default VLAN **** Extract only lines that start with LAN_ or DEFAULT_VLAN_ID from the defaultVLAN section
  const rawDefaultVLANLines =
    sectionConfigs.find(s => s.name === 'defaultVLAN')?.lines || [];
  const filteredDefaultVLAN = rawDefaultVLANLines.filter(
    line => line.startsWith('LAN_') || line.startsWith('DEFAULT_VLAN_ID=')
  );

  return {
    ...parsedSections,
    defaultVLANLines: filteredDefaultVLAN, // <-- include this new array
    deviceOverview,
    syslogHostStatus: syslogHostLine,
    sfpStatus,
    wanStatus: wanLines,
    lanStatus: lanLines,
    cidrRanges: cidrRangesLines,
    formattedDhcpLeases1: boldedOutput,
    formattedDhcpLeases: parsedDhcpLeases,
    rawDhcpLeases: rawDhcpLines.join('\n'),
    formattedArpTable: parsedArpTable,
    priWanFailoverLines,
    secWanFailoverLines,
  };
};
